import type {ContentEntryResponse, DisclaimerResponse, FileMetadataResponse, ImageUrlsResponse, TopicResponse} from "./feedService";
import type {PropertyMap} from "../../common/utils/objects";
import type {FeedChannel, FeedChannelWidth} from "./feedCommons";
import type {Maybe, Nullable} from "../../bootstrap/common/types";
import {falsyToUndefined} from "../../common/utils/basics";

export class FeedEntry {

    private channel: FeedEntryChannel;
    public recentlyVisited?: boolean;

    public constructor(public readonly data: FeedEntryData, private sources: string[] = []) {
        this.channel = DEFAULT_FEED_ENTRY_CHANNEL;
    }

    public static fromResponse(response: ContentEntryResponse): FeedEntry {
        return new FeedEntry(mapResponseToFeedEntryData(response), response.source);
    }

    public inChannelContext(allChannels: PropertyMap<FeedChannel>): this {
        const source = this.sources.findFirst(s => Object.keys(allChannels).includes(s)) ?? "";
        this.channel = mapChannel(allChannels[source]);
        return this;
    }

    public beingRecentlyVisited(value: boolean): this {
        this.recentlyVisited = value;
        return this;
    }

    public getImageUrl(force: boolean = true): string {
        let imageUrl: Maybe<string> = undefined;
        if (force || this.data.hasSpecificImage) {
            imageUrl = this.data.imageUrl || "";
        }
        return imageUrl || "";
    }

    public getImageUrlFor7x4Purpose(force: boolean = true): string {
        return this.getImageUrlForAspectRatio(force, urls => urls?.src_7x4);
    }

    public getImageUrlFor4x3Purpose(force: boolean = true): string {
        return this.getImageUrlForAspectRatio(force, urls => urls?.src_4x3);
    }

    public getImageUrlFor3x4Purpose(force: boolean = true): string {
        return this.getImageUrlForAspectRatio(force, urls => urls?.src_3x4);
    }

    private getImageUrlForAspectRatio(force: boolean, selector: (imageUrls: Maybe<ImageUrls>) => Maybe<string>): string {
        let imageUrl: Maybe<string> = undefined;
        if (force || this.data.hasSpecificImage) {
            imageUrl = selector(this.data.imageUrlsPerAspectRatio) || this.data.imageUrl;
        }
        return imageUrl || "";
    }

    public getTileStyle(): TileVariant {
        return this.channel.tileVariant;
    }

    public isExternalLink(): boolean {
        return this.data.newsType === "";
    }

    public pageTypeDescription(): string {
        if (this.data.newsType) {
            return this.data.newsType;
        }

        switch (this.data.contentType) {
            case "twitter":
                return "Twitter";
            case "facebook":
                return "Facebook";
            case "instagram":
                return "Instagram";
            case "linkedin":
                return "LinkedIn";
            case "youtube":
                return "YouTube";
            default:
                return "";
        }
    }
}

function mapResponseToFeedEntryData(entry: ContentEntryResponse): FeedEntryData {
    return {
        title: falsyToUndefined(entry.title),
        description: falsyToUndefined(entry.description),
        headline: falsyToUndefined(entry.headline),
        subHeadline: falsyToUndefined(entry.subheadline),
        url: entry.url ?? "",
        publishedDate: entry.publishedDate,
        imageUrl: falsyToUndefined(entry.imageUrl),
        imageUrlsPerAspectRatio: mapImageUrls(entry.imageUrls),
        hasSpecificImage: entry.hasSpecificImage,
        imageAltText: entry.imageAltText ?? "",
        contentType: entry.contentType ?? "",
        mediaType: entry.mediaType ?? "",
        newsType: entry.newsType ?? "",
        principalKeyword: entry.principalKeyword,
        topics: mapTopics(entry.topics),
        fileMetadata: mapFileMetadata(entry.fileMetadata),
        disclaimer: mapDisclaimer(entry.disclaimer),
        lightboxImageUrl: falsyToUndefined(entry.lightboxImageUrl),
        isNew: entry.isNew
    };
}

function mapChannel(channel: Maybe<FeedChannel>): FeedEntryChannel {
    return {
        tileVariant: channel?.width ? mapTileVariant(channel.width) : TileVariant.SMALL
    };
}

function mapTileVariant(variant: FeedChannelWidth): TileVariant {
    switch (variant) {
        case "small":
            return TileVariant.SMALL;
        case "large":
            return TileVariant.LARGE;
        case "double":
            return TileVariant.DOUBLE;
        default:
            return TileVariant.SMALL;
    }
}

function mapImageUrls(responseUrls: Nullable<ImageUrlsResponse>): Maybe<ImageUrls> {
    if (!responseUrls) {
        return undefined;
    }

    return {
        src_7x4: falsyToUndefined(responseUrls["7x4"]),
        src_4x3: falsyToUndefined(responseUrls["4x3"]),
        src_3x4: falsyToUndefined(responseUrls["3x4"])
    };
}

function mapTopics(topicsResponse: Nullable<TopicResponse[]>): FeedEntryTopic[] {
    if (!topicsResponse) {
        return [];
    }

    return topicsResponse.map(topic => ({name: topic.name, url: topic.url}));
}

function mapFileMetadata(metadataResponse: Nullable<FileMetadataResponse>): Maybe<FeedEntryFileMetadata> {
    if (!metadataResponse) {
        return undefined;
    }

    return {
        size: metadataResponse.size ?? "",
        extension: metadataResponse.extension ?? ""
    };
}

function mapDisclaimer(disclaimerResponse: Nullable<DisclaimerResponse>): Maybe<FeedEntryDisclaimer> {
    if (!disclaimerResponse) {
        return undefined;
    }

    return {
        headline: disclaimerResponse.headline ?? "",
        content: disclaimerResponse.content
    };
}

export type FeedEntryData = {
    title?: string;
    description?: string;
    headline?: string;
    subHeadline?: string;
    url: string;
    publishedDate: number;
    imageUrl?: string;
    imageUrlsPerAspectRatio?: ImageUrls;
    hasSpecificImage: boolean;
    imageAltText: string;
    contentType: string;
    mediaType: string;
    newsType: string;
    principalKeyword: string;
    topics: FeedEntryTopic[];
    fileMetadata?: FeedEntryFileMetadata;
    disclaimer?: FeedEntryDisclaimer;
    lightboxImageUrl?: string;
    isNew: boolean;
}

export type ImageUrls = {
    src_7x4?: string;
    src_4x3?: string;
    src_3x4?: string;
}

type FeedEntryDisclaimer = {
    headline: string;
    content: any;
}

type FeedEntryFileMetadata = {
    size: string;
    extension: string;
}

export type FeedEntryTopic = {
    name: string;
    url: string;
}

export enum TileVariant {
    SMALL = "small-tile",
    LARGE = "large-tile",
    DOUBLE = "double-tile"
}

type FeedEntryChannel = {
    tileVariant: TileVariant;
}

const DEFAULT_FEED_ENTRY_CHANNEL: FeedEntryChannel = {
    tileVariant: TileVariant.SMALL
};